import { pushToastMessage } from '@borg/ui';
import {
  ApplicationActions,
  AsyncState,
  JobApplicationData,
  JobApplicationFooBar,
  JobApplicationSource,
} from '@borg/types';
import { wait } from '@borg/utils';

export type ViewState =
  | 'form'
  | 'questionnaire'
  | 'overview'
  | 'verify'
  | 'success-recommended-jobs'
  | 'success-confirm-account'
  | 'success-save-search';

export const _applicationForm = createInjector('ApplicationForm', () => {
  const { t } = useI18n();
  const route = useRoute();

  const application = ref<JobApplicationFooBar | null>(null);
  const viewState = ref<ViewState>((route.query.viewState as ViewState) ?? 'form');
  const applicationActions = ref<ApplicationActions>();
  const startApplicationStatus = ref<AsyncState>('isInitial');
  const saveStatus = ref<AsyncState>('isInitial');
  const completeStatus = ref<AsyncState>('isInitial');
  const startVerificationStatus = ref<AsyncState>('isInitial');
  const resendVerificationStatus = ref<AsyncState>('isInitial');
  const resendVerificationCounter = ref<number>(0);
  const resendVerificationCounterRunning = ref<boolean>(false);
  const invalidCodeError = ref('');

  const hasQuestionnaire = computed(() => !!application.value?.questionnaire?.questions.length);
  const needsVerification = computed(() => application.value?.needsVerification ?? false);

  function goTo(vs: ViewState) {
    viewState.value = vs;
    return navigate({ query: { viewState: vs } }, { replace: true });
  }

  async function startApplication(jobUuid: string, source?: JobApplicationSource) {
    startApplicationStatus.value = 'inProgress';
    application.value = null;

    if (!jobUuid.length) {
      startApplicationStatus.value = 'isInvalid';
      return;
    }

    try {
      application.value = await apiFetch(`/jobs/${jobUuid}/applications/me`, {
        query: { source },
        method: 'GET',
      });
      startApplicationStatus.value = 'isSuccess';
    } catch (error) {
      startApplicationStatus.value = 'hasError';
      const { message, status } = parseApiFetchError(error);

      if (!handleAlreadyAppliedError(error)) {
        logger.error(message ?? `Error starting application`, {
          jobId: route.params.jobId?.toString(),
          message: message ?? 'unknown',
          status: status ?? 'unknown',
        });
        pushToastMessage({
          type: 'alert',
          title: t('JOB_APPLICATION_OVERVIEW_PAGE.START_APPLICATION_ERROR_TOAST.TITLE'),
          subtitle: t('JOB_APPLICATION_OVERVIEW_PAGE.START_APPLICATION_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  async function startVerification() {
    startVerificationStatus.value = 'inProgress';
    if (application.value?.id) {
      try {
        await apiFetch(`/applications/${application.value.id}/start-verification`, {
          method: 'POST',
        });
        startVerificationStatus.value = 'isSuccess';
      } catch (error) {
        startVerificationStatus.value = 'hasError';
        const { message } = parseApiFetchError(error);

        if (!handleAlreadyAppliedError(error)) {
          logger.error(message ?? `Error starting verification`, {
            jobId: route.params.jobId?.toString(),
            applicationId: application.value.id,
          });
          pushToastMessage({
            type: 'alert',
            title: t('JOB_APPLICATION_OVERVIEW_PAGE.SEND_APPLICATION_ERROR_TOAST.TITLE'),
            subtitle: t('JOB_APPLICATION_OVERVIEW_PAGE.SEND_APPLICATION_ERROR_TOAST.SUBTITLE'),
          });
        }
      }
    } else {
      startVerificationStatus.value = 'isInvalid';
    }
  }

  async function resendVerification() {
    resendVerificationStatus.value = 'inProgress';
    startResendVerificationCountdown();
    if (application.value?.id) {
      try {
        await apiFetch(`/applications/${application.value.id}/resend-verification`, {
          method: 'POST',
        });
        resendVerificationStatus.value = 'isSuccess';
        pushToastMessage({
          type: 'success',
          title: t(
            'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_SUCCESS_TOAST.TITLE',
          ),
          subtitle: t(
            'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_SUCCESS_TOAST.SUBTITLE',
          ),
        });
      } catch (error) {
        resendVerificationStatus.value = 'hasError';

        const { status, message, data } = parseApiFetchError(error);

        if (status === 409 && data.seconds) {
          pushToastMessage({
            type: 'warning',
            title: t(
              'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_TIMEOUT_TOAST.TITLE',
            ),
            subtitle: t(
              'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_TIMEOUT_TOAST.SUBTITLE',
              { seconds: data.seconds },
            ),
          });
          if (typeof data.seconds === 'number') {
            setResendVerificationCounter(data.seconds);
          }
        } else if (!handleAlreadyAppliedError(error)) {
          logger.error(message ?? `Error resending verification`, {
            jobId: route.params.jobId?.toString(),
            applicationId: application.value.id,
          });
          pushToastMessage({
            type: 'alert',
            title: t(
              'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_ERROR_TOAST.TITLE',
            ),
            subtitle: t(
              'JOB_APPLICATION_CODE_VERIFICATION_PAGE.RESEND_VERIFICATION_ERROR_TOAST.SUBTITLE',
            ),
          });
        }
      }
    } else {
      resendVerificationStatus.value = 'isInvalid';
    }
  }

  async function startResendVerificationCountdown() {
    setResendVerificationCounter();
    resendVerificationCounterRunning.value = true;
    while (resendVerificationCounter.value > 0) {
      await wait(1000);
      resendVerificationCounter.value = resendVerificationCounter.value - 1;
    }
    resendVerificationCounterRunning.value = false;
  }

  function setResendVerificationCounter(seconds = 30) {
    resendVerificationCounter.value = seconds;
  }

  async function save(data: Partial<JobApplicationData>) {
    saveStatus.value = 'inProgress';
    if (application.value) {
      application.value.data = { ...application.value.data, ...data };
      try {
        await apiFetch(`/applications/${application.value.id}`, {
          method: 'PUT',
          body: { ...application.value.data },
        });
        saveStatus.value = 'isSuccess';
      } catch (error) {
        saveStatus.value = 'hasError';
        const { message } = parseApiFetchError(error);
        logger.error(message ?? `Error saving application`, {
          jobId: route.params.jobId?.toString(),
          applicationId: application.value.id,
        });
      }
    } else {
      saveStatus.value = 'isInvalid';
    }
  }

  const saveDebounced = useDebounceFn(save, 500);

  async function complete(verificationCode?: string, providedApplicationId?: string) {
    completeStatus.value = 'inProgress';
    applicationActions.value = undefined;
    const applicationId = application.value ? application.value.id : providedApplicationId;
    if (applicationId) {
      try {
        const result = await apiFetch<ApplicationActions>(
          `/applications/${applicationId}/complete`,
          {
            method: 'POST',
            body: { verificationCode },
          },
        );
        if (result) {
          completeStatus.value = 'isSuccess';
          applicationActions.value = result;
        } else {
          completeStatus.value = 'isInvalid';
          pushToastMessage({
            type: 'alert',
            title: t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.VERIFICATION_INVALID_TOAST.TITLE'),
            subtitle: t(
              'JOB_APPLICATION_CODE_VERIFICATION_PAGE.VERIFICATION_INVALID_TOAST.SUBTITLE',
            ),
          });
        }
      } catch (error) {
        completeStatus.value = 'hasError';
        const { message } = parseApiFetchError(error);
        if (!handleAlreadyAppliedError(error)) {
          logger.error(message ?? `Error completing application`, {
            jobId: route.params.jobId?.toString(),
            applicationId,
          });
          pushToastMessage({
            type: 'alert',
            title: t('JOB_APPLICATION_OVERVIEW_PAGE.SEND_APPLICATION_ERROR_TOAST.TITLE'),
            subtitle: t('JOB_APPLICATION_OVERVIEW_PAGE.SEND_APPLICATION_ERROR_TOAST.SUBTITLE'),
          });
        }
      }
    } else {
      completeStatus.value = 'isInvalid';
    }
  }

  function handleAlreadyAppliedError(error: unknown) {
    const { data, status } = parseApiFetchError(error);
    const alreadyApplied =
      status === 409 &&
      data?.error === 'Conflict' &&
      data?.message === 'job_application_already_submitted';

    if (alreadyApplied) {
      pushToastMessage({
        type: 'alert',
        title: t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.ALREADY_APPLIED.TITLE'),
        subtitle: t('JOB_APPLICATION_CODE_VERIFICATION_PAGE.ALREADY_APPLIED.SUBTITLE'),
      });
    }
    return alreadyApplied;
  }

  return reactive({
    application,
    viewState,
    applicationActions,
    startApplicationStatus,
    saveStatus,
    startVerificationStatus,
    resendVerificationStatus,
    resendVerificationCounter,
    resendVerificationCounterRunning,
    completeStatus,
    invalidCodeError,
    hasQuestionnaire,
    needsVerification,
    goTo,
    startApplication,
    save,
    saveDebounced,
    startVerification,
    resendVerification,
    complete,
  });
});
